/**
 * Git Engine - симулятор Git команд для браузерного терминала
 *
 * Реализует логику основных Git команд без использования файловой системы.
 * Состояние хранится в памяти и синхронизируется с gitStore.
 */

import type { RepoState, CommitNode, StagedFile, WorkingFile, CommandResult } from '@/types/git';

/** Генерация уникального SHA-подобного идентификатора */
function generateSha(): string {
  return Math.random().toString(16).slice(2, 9);
}

/** Форматирование даты для Git вывода */
function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    year: 'numeric',
  });
}

/**
 * Класс GitEngine - симулятор Git репозитория
 */
export class GitEngine {
  private state: RepoState;
  private authorName: string = 'User';
  private authorEmail: string = 'user@example.com';

  constructor(initialState?: Partial<RepoState>) {
    this.state = {
      initialized: false,
      commits: {},
      branches: {},
      head: null,
      isDetached: false,
      staging: [],
      workingDir: [],
      remotes: {},
      ...initialState,
    };
  }

  /** Получить текущее состояние репозитория */
  getState(): RepoState {
    return { ...this.state };
  }

  /** Установить состояние репозитория */
  setState(state: RepoState): void {
    this.state = state;
  }

  /** Установить автора для коммитов */
  setAuthor(name: string, email: string): void {
    this.authorName = name;
    this.authorEmail = email;
  }

  /** Выполнить команду */
  execute(command: string): CommandResult {
    const trimmed = command.trim();

    // Парсим команду
    if (!trimmed.startsWith('git ')) {
      return {
        success: false,
        output: '',
        error: `git: '${trimmed}' is not a git command. See 'git --help'.`,
      };
    }

    const parts = trimmed.slice(4).split(/\s+/);
    const subcommand = parts[0];
    const args = parts.slice(1);

    // Маршрутизация команд
    switch (subcommand) {
      case 'init':
        return this.cmdInit(args);
      case 'status':
        return this.cmdStatus(args);
      case 'add':
        return this.cmdAdd(args);
      case 'commit':
        return this.cmdCommit(args);
      case 'log':
        return this.cmdLog(args);
      case 'branch':
        return this.cmdBranch(args);
      case 'checkout':
        return this.cmdCheckout(args);
      case 'switch':
        return this.cmdSwitch(args);
      case 'merge':
        return this.cmdMerge(args);
      case 'reset':
        return this.cmdReset(args);
      case 'revert':
        return this.cmdRevert(args);
      case 'rm':
        return this.cmdRm(args);
      case 'mv':
        return this.cmdMv(args);
      case 'remote':
        return this.cmdRemote(args);
      case 'fetch':
        return this.cmdFetch(args);
      case 'pull':
        return this.cmdPull(args);
      case 'push':
        return this.cmdPush(args);
      case 'stash':
        return this.cmdStash(args);
      case 'config':
        return this.cmdConfig(args);
      case 'diff':
        return this.cmdDiff(args);
      case 'help':
        return this.cmdHelp(args);
      default:
        return {
          success: false,
          output: '',
          error: `git: '${subcommand}' is not a git command. See 'git --help'.`,
        };
    }
  }

  // ==========================================
  // Команды Git
  // ==========================================

  /** git init - инициализация репозитория */
  private cmdInit(args: string[]): CommandResult {
    if (this.state.initialized) {
      return {
        success: true,
        output: 'Reinitialized existing Git repository in /project/.git/',
      };
    }

    this.state = {
      ...this.state,
      initialized: true,
      branches: { main: '' },
      head: 'main',
      isDetached: false,
    };

    return {
      success: true,
      output: 'Initialized empty Git repository in /project/.git/',
      stateChange: { initialized: true, branches: { main: '' }, head: 'main' },
    };
  }

  /** git status - состояние файлов */
  private cmdStatus(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const lines: string[] = [];
    const branch = this.state.isDetached
      ? `HEAD detached at ${this.state.head?.slice(0, 7)}`
      : `On branch ${this.state.head ?? 'main'}`;

    lines.push(branch);
    lines.push('');

    // Staged files
    if (this.state.staging.length > 0) {
      lines.push('Changes to be committed:');
      lines.push('  (use "git rm --cached <file>..." to unstage)');
      for (const file of this.state.staging) {
        const statusLabel = this.getStatusLabel(file.status);
        lines.push(`        ${statusLabel}: ${file.path}`);
      }
      lines.push('');
    }

    // Working directory changes
    const modifiedFiles = this.state.workingDir.filter((f) => f.status === 'modified');
    const untrackedFiles = this.state.workingDir.filter((f) => f.status === 'untracked');
    const deletedFiles = this.state.workingDir.filter((f) => f.status === 'deleted');

    if (modifiedFiles.length > 0 || deletedFiles.length > 0) {
      lines.push('Changes not staged for commit:');
      lines.push('  (use "git add <file>..." to update what will be committed)');
      lines.push('  (use "git restore <file>..." to discard changes in working directory)');
      for (const file of modifiedFiles) {
        lines.push(`        modified:   ${file.path}`);
      }
      for (const file of deletedFiles) {
        lines.push(`        deleted:    ${file.path}`);
      }
      lines.push('');
    }

    if (untrackedFiles.length > 0) {
      lines.push('Untracked files:');
      lines.push('  (use "git add <file>..." to include in what will be committed)');
      for (const file of untrackedFiles) {
        lines.push(`        ${file.path}`);
      }
      lines.push('');
    }

    if (this.state.staging.length === 0 && this.state.workingDir.length === 0) {
      const headSha = this.state.isDetached
        ? this.state.head
        : this.state.branches[this.state.head ?? 'main'];

      if (!headSha) {
        lines.push('No commits yet');
        lines.push('');
        lines.push('nothing to commit (create/copy files and use "git add" to track)');
      } else {
        lines.push('nothing to commit, working tree clean');
      }
    }

    return {
      success: true,
      output: lines.join('\n'),
    };
  }

  /** git add - добавление файлов в staging */
  private cmdAdd(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    if (args.length === 0) {
      return {
        success: false,
        output: '',
        error: 'Nothing specified, nothing added.',
      };
    }

    // Обработка git add . (добавить все файлы)
    if (args[0] === '.' || args[0] === '--all' || args[0] === '-A') {
      const newStaging: StagedFile[] = this.state.workingDir.map((file) => ({
        path: file.path,
        status:
          file.status === 'untracked'
            ? 'added'
            : file.status === 'deleted'
              ? 'deleted'
              : 'modified',
        sha: generateSha(),
      }));

      this.state = {
        ...this.state,
        staging: [...this.state.staging, ...newStaging],
        workingDir: [],
      };

      return {
        success: true,
        output: '',
        stateChange: { staging: this.state.staging, workingDir: [] },
      };
    }

    // Добавление конкретных файлов
    const addedFiles: string[] = [];
    const notFound: string[] = [];

    for (const path of args) {
      const fileIndex = this.state.workingDir.findIndex((f) => f.path === path);
      if (fileIndex >= 0) {
        const file = this.state.workingDir[fileIndex];
        const stagedFile: StagedFile = {
          path: file.path,
          status:
            file.status === 'untracked'
              ? 'added'
              : file.status === 'deleted'
                ? 'deleted'
                : 'modified',
          sha: generateSha(),
        };

        // Удаляем из workingDir и добавляем в staging
        const newWorkingDir = this.state.workingDir.filter((_, i) => i !== fileIndex);
        this.state = {
          ...this.state,
          staging: [...this.state.staging, stagedFile],
          workingDir: newWorkingDir,
        };

        addedFiles.push(path);
      } else {
        notFound.push(path);
      }
    }

    if (notFound.length > 0) {
      return {
        success: false,
        output: '',
        error: `fatal: pathspec '${notFound[0]}' did not match any files`,
      };
    }

    return {
      success: true,
      output: '',
      stateChange: { staging: this.state.staging, workingDir: this.state.workingDir },
    };
  }

  /** git commit - создание коммита */
  private cmdCommit(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    if (this.state.staging.length === 0) {
      return {
        success: false,
        output: '',
        error: 'nothing to commit, working tree clean',
      };
    }

    // Получаем сообщение коммита
    let message = '';
    const messageIndex = args.indexOf('-m');
    if (messageIndex >= 0 && args[messageIndex + 1]) {
      message = args[messageIndex + 1];
    } else if (args[0]?.startsWith('-m"') || args[0]?.startsWith("-m'")) {
      message = args[0].slice(3, -1);
    } else {
      return {
        success: false,
        output: '',
        error: "error: switch `m' requires a value",
      };
    }

    // Создаём коммит
    const sha = generateSha();
    const parentSha = this.state.isDetached
      ? this.state.head
      : this.state.branches[this.state.head ?? 'main'];

    const commit: CommitNode = {
      sha,
      parents: parentSha ? [parentSha] : [],
      tree: generateSha(),
      message,
      author: this.authorName,
      authorEmail: this.authorEmail,
      timestamp: Date.now(),
      branchLabels: [],
    };

    // Обновляем состояние
    const newCommits = { ...this.state.commits, [sha]: commit };
    const currentBranch = this.state.isDetached ? null : this.state.head;

    const newBranches = currentBranch
      ? { ...this.state.branches, [currentBranch]: sha }
      : this.state.branches;

    this.state = {
      ...this.state,
      commits: newCommits,
      branches: newBranches,
      staging: [],
      head: this.state.isDetached ? sha : this.state.head,
    };

    const branchInfo = currentBranch ? ` ${currentBranch}` : '';
    const filesCount = Object.keys(this.state.commits).length === 0 ? 1 : this.state.staging.length;

    return {
      success: true,
      output: `[${branchInfo} ${sha.slice(0, 7)}] ${message}
 ${filesCount} file changed, ${Math.floor(Math.random() * 10) + 1} insertions(+)`,
      stateChange: {
        commits: newCommits,
        branches: newBranches,
        staging: [],
      },
    };
  }

  /** git log - история коммитов */
  private cmdLog(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const headSha = this.state.isDetached
      ? this.state.head
      : this.state.branches[this.state.head ?? 'main'];

    if (!headSha) {
      return {
        success: true,
        output: "fatal: your current branch 'main' does not have any commits yet",
      };
    }

    const lines: string[] = [];
    const visited = new Set<string>();
    const queue = [headSha];

    while (queue.length > 0) {
      const sha = queue.shift()!;
      if (visited.has(sha)) continue;
      visited.add(sha);

      const commit = this.state.commits[sha];
      if (!commit) continue;

      // Форматирование коммита
      lines.push(`commit ${sha}`);

      // Добавляем метки веток
      const branchLabels: string[] = [];
      for (const [branchName, branchSha] of Object.entries(this.state.branches)) {
        if (branchSha === sha) {
          branchLabels.push(branchName);
        }
      }
      if (branchLabels.length > 0) {
        lines.push(`Author: ${commit.author} <${commit.authorEmail}>`);
        lines.push(`Date:   ${formatDate(commit.timestamp)}`);
        lines.push('');
        lines.push(`    ${commit.message}`);
        lines.push('');
      }

      // Добавляем родителей в очередь
      for (const parentSha of commit.parents) {
        queue.push(parentSha);
      }
    }

    return {
      success: true,
      output: lines.join('\n'),
    };
  }

  /** git branch - управление ветками */
  private cmdBranch(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    // git branch - список веток
    if (args.length === 0) {
      const lines: string[] = [];
      for (const [name, sha] of Object.entries(this.state.branches)) {
        const isCurrent = !this.state.isDetached && this.state.head === name;
        lines.push(`${isCurrent ? '* ' : '  '}${name}`);
      }
      return {
        success: true,
        output: lines.join('\n'),
      };
    }

    // git branch -d <name> - удаление ветки
    if (args[0] === '-d' || args[0] === '-D') {
      const branchName = args[1];
      if (!branchName) {
        return {
          success: false,
          output: '',
          error: 'error: branch name required',
        };
      }

      if (!this.state.branches[branchName]) {
        return {
          success: false,
          output: '',
          error: `error: branch '${branchName}' not found.`,
        };
      }

      if (this.state.head === branchName) {
        return {
          success: false,
          output: '',
          error: `error: Cannot delete branch '${branchName}' checked out at`,
        };
      }

      const { [branchName]: _, ...remainingBranches } = this.state.branches;
      this.state = {
        ...this.state,
        branches: remainingBranches,
      };

      return {
        success: true,
        output: `Deleted branch ${branchName} (was ${this.state.branches[branchName]?.slice(0, 7) ?? '0000000'}).`,
        stateChange: { branches: remainingBranches },
      };
    }

    // git branch <name> - создание ветки
    const branchName = args[0];
    if (this.state.branches[branchName]) {
      return {
        success: false,
        output: '',
        error: `fatal: A branch named '${branchName}' already exists.`,
      };
    }

    const headSha = this.state.isDetached
      ? this.state.head
      : this.state.branches[this.state.head ?? 'main'];

    this.state = {
      ...this.state,
      branches: {
        ...this.state.branches,
        [branchName]: headSha ?? '',
      },
    };

    return {
      success: true,
      output: '',
      stateChange: { branches: this.state.branches },
    };
  }

  /** git checkout - переключение веток */
  private cmdCheckout(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    // git checkout -b <name> - создание и переключение
    if (args[0] === '-b') {
      const branchName = args[1];
      if (!branchName) {
        return {
          success: false,
          output: '',
          error: 'fatal: branch name required',
        };
      }

      if (this.state.branches[branchName]) {
        return {
          success: false,
          output: '',
          error: `fatal: A branch named '${branchName}' already exists.`,
        };
      }

      const headSha = this.state.isDetached
        ? this.state.head
        : this.state.branches[this.state.head ?? 'main'];

      this.state = {
        ...this.state,
        branches: {
          ...this.state.branches,
          [branchName]: headSha ?? '',
        },
        head: branchName,
        isDetached: false,
      };

      return {
        success: true,
        output: `Switched to a new branch '${branchName}'`,
        stateChange: { branches: this.state.branches, head: branchName, isDetached: false },
      };
    }

    // git checkout <branch> - переключение на ветку
    const target = args[0];
    if (!target) {
      return {
        success: false,
        output: '',
        error: 'error: you must specify a branch name',
      };
    }

    if (this.state.branches[target]) {
      this.state = {
        ...this.state,
        head: target,
        isDetached: false,
      };

      return {
        success: true,
        output: `Switched to branch '${target}'`,
        stateChange: { head: target, isDetached: false },
      };
    }

    // Попытка переключиться на коммит (detached HEAD)
    if (this.state.commits[target]) {
      this.state = {
        ...this.state,
        head: target,
        isDetached: true,
      };

      return {
        success: true,
        output: `Note: switching to '${target}'.

You are in 'detached HEAD' state. You can look around, make experimental
changes and commit them, and you can discard any commits you make in this
state without impacting any branches by switching back to a branch.

HEAD is now at ${target.slice(0, 7)} ${this.state.commits[target].message}`,
        stateChange: { head: target, isDetached: true },
      };
    }

    return {
      success: false,
      output: '',
      error: `error: pathspec '${target}' did not match any file(s) known to git`,
    };
  }

  /** git switch - переключение веток (новый синтаксис) */
  private cmdSwitch(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    // git switch -c <name> - создание и переключение
    if (args[0] === '-c' || args[0] === '-C') {
      const branchName = args[1];
      if (!branchName) {
        return {
          success: false,
          output: '',
          error: 'fatal: branch name required',
        };
      }

      if (this.state.branches[branchName] && args[0] !== '-C') {
        return {
          success: false,
          output: '',
          error: `fatal: A branch named '${branchName}' already exists.`,
        };
      }

      const headSha = this.state.isDetached
        ? this.state.head
        : this.state.branches[this.state.head ?? 'main'];

      this.state = {
        ...this.state,
        branches: {
          ...this.state.branches,
          [branchName]: headSha ?? '',
        },
        head: branchName,
        isDetached: false,
      };

      return {
        success: true,
        output: `Switched to a new branch '${branchName}'`,
        stateChange: { branches: this.state.branches, head: branchName, isDetached: false },
      };
    }

    // git switch <branch>
    const target = args[0];
    if (!target) {
      return {
        success: false,
        output: '',
        error: 'error: you must specify a branch name',
      };
    }

    if (this.state.branches[target]) {
      this.state = {
        ...this.state,
        head: target,
        isDetached: false,
      };

      return {
        success: true,
        output: `Switched to branch '${target}'`,
        stateChange: { head: target, isDetached: false },
      };
    }

    return {
      success: false,
      output: '',
      error: `fatal: invalid reference: ${target}`,
    };
  }

  /** git merge - слияние веток */
  private cmdMerge(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const sourceBranch = args[0];
    if (!sourceBranch) {
      return {
        success: false,
        output: '',
        error: 'error: missing source branch',
      };
    }

    if (!this.state.branches[sourceBranch]) {
      return {
        success: false,
        output: '',
        error: `merge: ${sourceBranch} - not something we can merge`,
      };
    }

    const currentBranch = this.state.head;
    if (this.state.isDetached || !currentBranch) {
      return {
        success: false,
        output: '',
        error: 'fatal: cannot merge in detached HEAD state',
      };
    }

    const currentSha = this.state.branches[currentBranch];
    const sourceSha = this.state.branches[sourceBranch];

    // Fast-forward merge
    if (!currentSha) {
      this.state = {
        ...this.state,
        branches: {
          ...this.state.branches,
          [currentBranch]: sourceSha,
        },
      };

      return {
        success: true,
        output: `Updating ${currentSha?.slice(0, 7) ?? '0000000'}..${sourceSha.slice(0, 7)}
Fast-forward
 1 file changed, ${Math.floor(Math.random() * 10) + 1} insertions(+)`,
        stateChange: { branches: this.state.branches },
      };
    }

    // Проверяем, является ли source потомком current (fast-forward)
    const isFastForward = this.isAncestor(currentSha, sourceSha);

    if (isFastForward) {
      this.state = {
        ...this.state,
        branches: {
          ...this.state.branches,
          [currentBranch]: sourceSha,
        },
      };

      return {
        success: true,
        output: `Updating ${currentSha.slice(0, 7)}..${sourceSha.slice(0, 7)}
Fast-forward
 1 file changed, ${Math.floor(Math.random() * 10) + 1} insertions(+)`,
        stateChange: { branches: this.state.branches },
      };
    }

    // Three-way merge - создаём merge commit
    const sha = generateSha();
    const mergeCommit: CommitNode = {
      sha,
      parents: [currentSha, sourceSha],
      tree: generateSha(),
      message: `Merge branch '${sourceBranch}' into ${currentBranch}`,
      author: this.authorName,
      authorEmail: this.authorEmail,
      timestamp: Date.now(),
      branchLabels: [],
    };

    this.state = {
      ...this.state,
      commits: {
        ...this.state.commits,
        [sha]: mergeCommit,
      },
      branches: {
        ...this.state.branches,
        [currentBranch]: sha,
      },
    };

    return {
      success: true,
      output: `Merge made by the 'ort' strategy.
 1 file changed, ${Math.floor(Math.random() * 10) + 1} insertions(+)`,
      stateChange: { commits: this.state.commits, branches: this.state.branches },
    };
  }

  /** git reset - отмена изменений */
  private cmdReset(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const mode = args[0];
    const target = args[1];

    // git reset (unstage)
    if (!mode || mode === '--mixed') {
      if (!target) {
        // Сбросить весь staging
        this.state = {
          ...this.state,
          staging: [],
        };
        return {
          success: true,
          output: 'Unstaged all changes.',
          stateChange: { staging: [] },
        };
      }
    }

    // git reset --hard
    if (mode === '--hard') {
      const headSha = this.state.isDetached
        ? this.state.head
        : this.state.branches[this.state.head ?? 'main'];

      this.state = {
        ...this.state,
        staging: [],
        workingDir: [],
      };

      return {
        success: true,
        output: 'HEAD is now at ' + (headSha?.slice(0, 7) ?? '0000000'),
        stateChange: { staging: [], workingDir: [] },
      };
    }

    // git reset --soft (отменить коммит, сохранить изменения в staging)
    if (mode === '--soft') {
      const headSha = this.state.isDetached
        ? this.state.head
        : this.state.branches[this.state.head ?? 'main'];

      if (!headSha) {
        return {
          success: false,
          output: '',
          error: 'fatal: cannot reset to empty history',
        };
      }

      const currentCommit = this.state.commits[headSha];
      if (!currentCommit) {
        return {
          success: false,
          output: '',
          error: 'fatal: commit not found',
        };
      }

      // Перемещаем HEAD на родителя
      const parentSha = currentCommit.parents[0];
      const currentBranch = this.state.head;

      if (!this.state.isDetached && currentBranch) {
        this.state = {
          ...this.state,
          branches: {
            ...this.state.branches,
            [currentBranch]: parentSha ?? '',
          },
        };
      } else {
        this.state = {
          ...this.state,
          head: parentSha ?? null,
        };
      }

      return {
        success: true,
        output: '',
        stateChange: { branches: this.state.branches, head: this.state.head },
      };
    }

    return {
      success: false,
      output: '',
      error: `error: unknown option '${mode}'`,
    };
  }

  /** git revert - отмена коммита через новый коммит */
  private cmdRevert(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const targetSha = args[0];
    if (!targetSha) {
      return {
        success: false,
        output: '',
        error: 'error: commit hash required',
      };
    }

    const commit = this.state.commits[targetSha];
    if (!commit) {
      return {
        success: false,
        output: '',
        error: `fatal: bad revision '${targetSha}'`,
      };
    }

    // Создаём revert commit
    const sha = generateSha();
    const headSha = this.state.isDetached
      ? this.state.head
      : this.state.branches[this.state.head ?? 'main'];

    const revertCommit: CommitNode = {
      sha,
      parents: headSha ? [headSha] : [],
      tree: generateSha(),
      message: `Revert "${commit.message}"\n\nThis reverts commit ${commit.sha}.`,
      author: this.authorName,
      authorEmail: this.authorEmail,
      timestamp: Date.now(),
      branchLabels: [],
    };

    const currentBranch = this.state.isDetached ? null : this.state.head;
    const newBranches = currentBranch
      ? { ...this.state.branches, [currentBranch]: sha }
      : this.state.branches;

    this.state = {
      ...this.state,
      commits: {
        ...this.state.commits,
        [sha]: revertCommit,
      },
      branches: newBranches,
      head: this.state.isDetached ? sha : this.state.head,
    };

    return {
      success: true,
      output: `[${currentBranch ?? 'HEAD'} ${sha.slice(0, 7)}] Revert "${commit.message}"`,
      stateChange: { commits: this.state.commits, branches: newBranches },
    };
  }

  /** git rm - удаление файлов */
  private cmdRm(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const cached = args.includes('--cached');
    const filePath = args.filter((a) => !a.startsWith('-'))[0];

    if (!filePath) {
      return {
        success: false,
        output: '',
        error: 'error: file path required',
      };
    }

    if (cached) {
      // Удалить только из staging
      const newStaging = this.state.staging.filter((f) => f.path !== filePath);
      this.state = {
        ...this.state,
        staging: newStaging,
      };

      return {
        success: true,
        output: `rm '${filePath}'`,
        stateChange: { staging: newStaging },
      };
    }

    // Удалить из staging и working directory
    const newStaging = this.state.staging.filter((f) => f.path !== filePath);
    const newWorkingDir = this.state.workingDir.filter((f) => f.path !== filePath);

    this.state = {
      ...this.state,
      staging: newStaging,
      workingDir: newWorkingDir,
    };

    return {
      success: true,
      output: `rm '${filePath}'`,
      stateChange: { staging: newStaging, workingDir: newWorkingDir },
    };
  }

  /** git mv - перемещение файлов */
  private cmdMv(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const source = args[0];
    const destination = args[1];

    if (!source || !destination) {
      return {
        success: false,
        output: '',
        error: 'error: source and destination required',
      };
    }

    // Проверяем наличие файла в staging или working dir
    const stagedFile = this.state.staging.find((f) => f.path === source);
    const workingFile = this.state.workingDir.find((f) => f.path === source);

    if (stagedFile) {
      const newStaging = this.state.staging.map((f) =>
        f.path === source
          ? { ...f, path: destination, oldPath: source, status: 'renamed' as const }
          : f
      );
      this.state = {
        ...this.state,
        staging: newStaging,
      };

      return {
        success: true,
        output: '',
        stateChange: { staging: newStaging },
      };
    }

    if (workingFile) {
      const newWorkingDir = this.state.workingDir.map((f) =>
        f.path === source
          ? { ...f, path: destination, oldPath: source, status: 'renamed' as const }
          : f
      );
      this.state = {
        ...this.state,
        workingDir: newWorkingDir,
      };

      return {
        success: true,
        output: '',
        stateChange: { workingDir: newWorkingDir },
      };
    }

    return {
      success: false,
      output: '',
      error: `fatal: not under version control, source=${source}, destination=${destination}`,
    };
  }

  /** git remote - управление remote репозиториями */
  private cmdRemote(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    // git remote - список remote
    if (args.length === 0) {
      const remotes = Object.keys(this.state.remotes);
      return {
        success: true,
        output: remotes.join('\n'),
      };
    }

    // git remote add <name> <url>
    if (args[0] === 'add') {
      const name = args[1];
      const url = args[2];

      if (!name || !url) {
        return {
          success: false,
          output: '',
          error: 'error: remote name and URL required',
        };
      }

      if (this.state.remotes[name]) {
        return {
          success: false,
          output: '',
          error: `error: remote ${name} already exists.`,
        };
      }

      this.state = {
        ...this.state,
        remotes: {
          ...this.state.remotes,
          [name]: url,
        },
      };

      return {
        success: true,
        output: '',
        stateChange: { remotes: this.state.remotes },
      };
    }

    // git remote remove <name>
    if (args[0] === 'remove' || args[0] === 'rm') {
      const name = args[1];
      if (!name) {
        return {
          success: false,
          output: '',
          error: 'error: remote name required',
        };
      }

      if (!this.state.remotes[name]) {
        return {
          success: false,
          output: '',
          error: `error: No such remote: ${name}`,
        };
      }

      const { [name]: _, ...remainingRemotes } = this.state.remotes;
      this.state = {
        ...this.state,
        remotes: remainingRemotes,
      };

      return {
        success: true,
        output: '',
        stateChange: { remotes: remainingRemotes },
      };
    }

    return {
      success: false,
      output: '',
      error: `error: unknown subcommand: ${args[0]}`,
    };
  }

  /** git fetch - получение изменений из remote */
  private cmdFetch(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const remoteName = args[0] ?? 'origin';

    if (!this.state.remotes[remoteName]) {
      return {
        success: false,
        output: '',
        error: `fatal: '${remoteName}' does not appear to be a git repository`,
      };
    }

    // Симуляция fetch - в реальности здесь было бы получение данных
    return {
      success: true,
      output: `From ${this.state.remotes[remoteName]}
 * [new branch]      main       -> origin/main`,
    };
  }

  /** git pull - получение и слияние изменений */
  private cmdPull(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const remoteName = args[0] ?? 'origin';

    if (!this.state.remotes[remoteName]) {
      return {
        success: false,
        output: '',
        error: `fatal: '${remoteName}' does not appear to be a git repository`,
      };
    }

    // Симуляция pull
    return {
      success: true,
      output: `From ${this.state.remotes[remoteName]}
 * branch            main       -> FETCH_HEAD
Updating ${this.state.branches[this.state.head ?? 'main']?.slice(0, 7) ?? '0000000'}..abc1234
Fast-forward
 1 file changed, ${Math.floor(Math.random() * 10) + 1} insertions(+)`,
    };
  }

  /** git push - отправка изменений в remote */
  private cmdPush(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const remoteName = args[0] ?? 'origin';
    const branchName = args[1] ?? this.state.head;

    if (!this.state.remotes[remoteName]) {
      return {
        success: false,
        output: '',
        error: `fatal: '${remoteName}' does not appear to be a git repository`,
      };
    }

    if (!branchName || !this.state.branches[branchName]) {
      return {
        success: false,
        output: '',
        error: `fatal: The current branch ${branchName} has no upstream branch.`,
      };
    }

    const sha = this.state.branches[branchName];

    return {
      success: true,
      output: `To ${this.state.remotes[remoteName]}
   0000000..${sha?.slice(0, 7) ?? '0000000'}  ${branchName} -> ${branchName}`,
    };
  }

  /** git stash - временное хранение изменений */
  private cmdStash(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    if (args[0] === 'pop' || args[0] === 'apply') {
      // Восстановить изменения
      return {
        success: true,
        output: 'Changes restored from stash.',
      };
    }

    if (args[0] === 'list') {
      return {
        success: true,
        output: 'stash@{0}: WIP on main: abc1234 Last commit message',
      };
    }

    // git stash - сохранить изменения
    if (this.state.workingDir.length === 0 && this.state.staging.length === 0) {
      return {
        success: false,
        output: '',
        error: 'No local changes to save',
      };
    }

    this.state = {
      ...this.state,
      staging: [],
      workingDir: [],
    };

    return {
      success: true,
      output: 'Saved working directory and index state WIP on main: abc1234 Last commit',
      stateChange: { staging: [], workingDir: [] },
    };
  }

  /** git config - конфигурация */
  private cmdConfig(args: string[]): CommandResult {
    if (args.length === 0) {
      return {
        success: false,
        output: '',
        error: 'error: no action specified',
      };
    }

    // git config user.name "Name"
    if (args[0] === 'user.name') {
      if (args[1]) {
        this.authorName = args[1];
        return { success: true, output: '' };
      }
      return { success: true, output: this.authorName };
    }

    // git config user.email "email"
    if (args[0] === 'user.email') {
      if (args[1]) {
        this.authorEmail = args[1];
        return { success: true, output: '' };
      }
      return { success: true, output: this.authorEmail };
    }

    return {
      success: false,
      output: '',
      error: `error: unknown config key: ${args[0]}`,
    };
  }

  /** git diff - показать различия */
  private cmdDiff(args: string[]): CommandResult {
    if (!this.state.initialized) {
      return {
        success: false,
        output: '',
        error: 'fatal: not a git repository (or any of the parent directories): .git',
      };
    }

    const lines: string[] = [];
    const isCached = args.includes('--cached') || args.includes('--staged');
    const filePath = args.find((a) => !a.startsWith('-'));

    // Получаем HEAD SHA
    const headSha = this.state.isDetached
      ? this.state.head
      : this.state.branches[this.state.head ?? 'main'];

    // git diff --cached / --staged - показать изменения в staging (vs HEAD)
    if (isCached) {
      if (this.state.staging.length === 0) {
        return {
          success: true,
          output: '',
        };
      }

      const filesToShow = filePath
        ? this.state.staging.filter((f) => f.path === filePath)
        : this.state.staging;

      for (const file of filesToShow) {
        lines.push(this.formatDiffHeader(file.path, 'HEAD', 'index'));
        lines.push(this.formatDiffContent(file, 'staged'));
        lines.push('');
      }

      return {
        success: true,
        output: lines.join('\n').trimEnd(),
      };
    }

    // git diff [file] - показать unstaged изменения (working dir vs staging/HEAD)
    const modifiedFiles = this.state.workingDir.filter(
      (f) => f.status === 'modified' || f.status === 'deleted'
    );

    if (modifiedFiles.length === 0) {
      return {
        success: true,
        output: '',
      };
    }

    const filesToShow = filePath
      ? modifiedFiles.filter((f) => f.path === filePath)
      : modifiedFiles;

    for (const file of filesToShow) {
      // Проверяем, есть ли файл в staging
      const stagedFile = this.state.staging.find((f) => f.path === file.path);
      const compareSource = stagedFile ? 'index' : headSha ? 'HEAD' : 'empty';

      lines.push(this.formatDiffHeader(file.path, compareSource, 'working tree'));
      lines.push(this.formatDiffContent(file, 'working'));
      lines.push('');
    }

    return {
      success: true,
      output: lines.join('\n').trimEnd(),
    };
  }

  /** Форматировать заголовок diff */
  private formatDiffHeader(path: string, source: string, target: string): string {
    return `diff --git a/${path} b/${path}
index 0000000..1234567 100644
--- a/${path}
+++ b/${path}`;
  }

  /** Форматировать содержимое diff */
  private formatDiffContent(
    file: StagedFile | WorkingFile,
    mode: 'staged' | 'working'
  ): string {
    const lines: string[] = [];

    // Симуляция diff-вывода
    if (file.status === 'deleted') {
      lines.push('@@ -1,5 +0,0 @@');
      lines.push('-// Previous content of the file');
      lines.push('-// This file was deleted');
      lines.push('-// Line 3');
      lines.push('-// Line 4');
      lines.push('-// Line 5');
    } else if (file.status === 'added') {
      lines.push('@@ -0,0 +1,5 @@');
      lines.push('+// New file content');
      lines.push('+// Added line 1');
      lines.push('+// Added line 2');
      lines.push('+// Added line 3');
      lines.push('+// Added line 4');
    } else if (file.status === 'modified' || file.status === 'renamed') {
      lines.push('@@ -1,5 +1,6 @@');
      lines.push(' // Existing content');
      lines.push(' // Line 2');
      lines.push('-// Old line 3');
      lines.push('+// Modified line 3');
      lines.push('+// New line added');
      lines.push(' // Line 4');
      lines.push(' // Line 5');
    }

    return lines.join('\n');
  }

  /** git help - справка */
  private cmdHelp(args: string[]): CommandResult {
    const commands = [
      'init',
      'status',
      'add',
      'commit',
      'log',
      'branch',
      'checkout',
      'switch',
      'merge',
      'reset',
      'revert',
      'rm',
      'mv',
      'remote',
      'fetch',
      'pull',
      'push',
      'stash',
      'config',
      'diff',
    ];

    return {
      success: true,
      output: `usage: git [--version] [--help] <command> [<args>]

These are common Git commands used in various situations:

start a working area (see also: git help tutorial)
   init     Create an empty Git repository or reinitialize an existing one

work on the current change (see also: git help everyday)
   add      Add file contents to the index
   mv       Move or rename a file, a directory, or a symlink
   reset    Reset current HEAD to the specified state
   rm       Remove files from the working tree and from the index

examine the history and state (see also: git help revisions)
   log      Show commit logs
   status   Show the working tree status

grow, mark and tweak your common history
   branch   List, create, or delete branches
   commit   Record changes to the repository
   merge    Join two or more development histories together
   rebase   Reapply commits on top of another base tip
   revert   Revert some existing commits

collaborate (see also: git help workflows)
   fetch    Download objects and refs from another repository
   pull     Fetch from and integrate with another repository or a local branch
   push     Update remote refs along with associated objects

'git help -a' and 'git help -g' list available subcommands and some
concept guides. See 'git help <command>' or 'git help <concept>'
to read about a specific subcommand or concept.`,
    };
  }

  // ==========================================
  // Вспомогательные методы
  // ==========================================

  /** Получить метку статуса для вывода */
  private getStatusLabel(status: StagedFile['status']): string {
    switch (status) {
      case 'added':
        return 'new file';
      case 'modified':
        return 'modified';
      case 'deleted':
        return 'deleted';
      case 'renamed':
        return 'renamed';
    }
  }

  /** Проверить, является ли commit предком potentialAncestor */
  private isAncestor(commitSha: string, potentialAncestorSha: string): boolean {
    const visited = new Set<string>();
    const queue = [commitSha];

    while (queue.length > 0) {
      const sha = queue.shift()!;
      if (sha === potentialAncestorSha) return true;
      if (visited.has(sha)) continue;
      visited.add(sha);

      const commit = this.state.commits[sha];
      if (commit) {
        for (const parent of commit.parents) {
          queue.push(parent);
        }
      }
    }

    return false;
  }

  // ==========================================
  // Методы для работы с файлами (симуляция)
  // ==========================================

  /** Добавить файл в рабочую директорию (для симуляции) */
  addFile(path: string, status: WorkingFile['status'] = 'untracked'): void {
    const existing = this.state.workingDir.find((f) => f.path === path);
    if (existing) {
      this.state = {
        ...this.state,
        workingDir: this.state.workingDir.map((f) => (f.path === path ? { ...f, status } : f)),
      };
    } else {
      this.state = {
        ...this.state,
        workingDir: [...this.state.workingDir, { path, status }],
      };
    }
  }

  /** Удалить файл из рабочей директории (для симуляции) */
  removeFile(path: string): void {
    const existing = this.state.workingDir.find((f) => f.path === path);
    if (existing) {
      this.state = {
        ...this.state,
        workingDir: this.state.workingDir.map((f) =>
          f.path === path ? { ...f, status: 'deleted' as const } : f
        ),
      };
    }
  }

  /** Сброс репозитория в начальное состояние */
  reset(): void {
    this.state = {
      initialized: false,
      commits: {},
      branches: {},
      head: null,
      isDetached: false,
      staging: [],
      workingDir: [],
      remotes: {},
    };
  }

  /** Инициализация репозитория (автоматический git init) */
  initializeRepo(): void {
    if (!this.state.initialized) {
      this.state = {
        ...this.state,
        initialized: true,
        branches: { main: '' },
        head: 'main',
        isDetached: false,
      };
    }
  }

  /** Добавить начальные файлы для урока */
  setupLessonFiles(files: Array<{ path: string; status: WorkingFile['status'] }>): void {
    this.state = {
      ...this.state,
      workingDir: files.map((f) => ({ path: f.path, status: f.status })),
    };
  }

  /** Загрузить предустановку Git-репозитория */
  loadPreset(preset: import('@/lib/git/presets/types').GitPreset): void {
    const commits: Record<string, CommitNode> = {};
    const branches: Record<string, string> = {};
    const remotes: Record<string, string> = {};

    // Добавляем коммиты
    for (const commit of preset.commits) {
      commits[commit.sha] = {
        sha: commit.sha,
        parents: commit.parents,
        tree: '', // Не используется в симуляции
        message: commit.message,
        author: commit.author,
        authorEmail: commit.authorEmail,
        timestamp: commit.timestamp,
        branchLabels: [],
      };
    }

    // Добавляем ветки
    for (const branch of preset.branches) {
      if (!branch.isRemote) {
        branches[branch.name] = branch.head;
      }
    }

    // Добавляем remotes
    if (preset.remotes) {
      for (const remote of preset.remotes) {
        remotes[remote.name] = remote.url;
        // Добавляем remote branches
        for (const [branchName, sha] of Object.entries(remote.branches)) {
          branches[`${remote.name}/${branchName}`] = sha;
        }
      }
    }

    // Формируем состояние
    this.state = {
      initialized: true,
      commits,
      branches,
      head: preset.currentBranch,
      isDetached: false,
      staging: preset.staging ?? [],
      workingDir: preset.workingDir ?? [],
      remotes,
    };
  }
}

// Экспортируем singleton экземпляр
export const gitEngine = new GitEngine();
